export const exploreColumnTypes = [
  "tenantId",
  "id",
  "displayId",
  "subject",
  "description",
  "createdAt",
  "updatedAt",
  "status",
  "channel",
  "topic",
  "subTopic",
  "category",
  "agentAssigned",
  "team",
  "teams",
  "tenantUser",
  "tenantOrganization",
  "tags",
  "avgQaScore",
  "avgQaScoreRoundRange",
  "avgQuackMessageScoreRoundRange",
  "avgAutoQaScore",
  "avgSentiment",
  "lastSentiment",
  "avgQuackMessageScore",
  "csat",
  "csatComment",
  "countPublicMessages",
  "countInternalMessages",
  "timeToResolution",
  "timeToFirstResponse",
  "resolutionStatus",
  "escalationReason",
  "skipReason",
  "resolvedReason",
  "countQuackMessages",
  "isQuackEscalated",
  "isQuackAnswered",
  "isAnsweredByAui",
  "collaborators",
  "isFiniteState",
  "validationStatus",
  "agentAssignedId",
  "ticketId",
  "originAuthor",
  "originUrl",
  "scoreTitle",
  "scoreContent",
  "simulatorId",
  "skillUsed",
  "skillsUsed",
  "isUserAuthenticated",
  "avgCsat",
] as const;
export type ExploreColumnType = (typeof exploreColumnTypes)[number];

export function isExploreColumnType(value: string): value is ExploreColumnType {
  return exploreColumnTypes.includes(value as ExploreColumnType);
}

export const exploreGroupByColumnTypes = [
  "tenantId",
  "status",
  "channel",
  "topic",
  "category",
  "resolutionStatus",
  "escalationReason",
  "skipReason",
  "resolvedReason",
  "subTopic",
  "agentAssigned",
  "team",
  "week",
  "day",
  "month",
  "avgQaScore",
  "avgQaScoreRoundRange",
  "avgQuackMessageScoreRoundRange",
  "avgSentiment",
  "isQuackAnswered",
  "isAnsweredByAui",
  "scoreTitle",
  "scoreContent",
  "originUrl",
  "simulator",
  "skillUsed",
  "avgCsat",
] as const;
export type ExploreGroupByColumnType =
  (typeof exploreGroupByColumnTypes)[number];

export function isExploreGroupByColumnType(
  value: string
): value is ExploreGroupByColumnType {
  return exploreGroupByColumnTypes.includes(value as ExploreGroupByColumnType);
}

export const exploreAggColumnTypes = [
  "volume",
  "volumeRatio",
  "avgQuackMessages",
  "avgQaScore",
  "avgAutoQaScore",
  "avgSentiment",
  "avgCsat",
  "avgPublicMessages",
  "avgInternalMessages",
  "countAgentsAssigned",
  "avgTimeToResolution",
  "avgTimeToFirstResponse",
  "avgQaScoreByTitle",
  "avgQaScoreByContent",
] as const;
export type ExploreAggColumnType = (typeof exploreAggColumnTypes)[number];

export function isExploreAggColumnType(
  value: string
): value is ExploreAggColumnType {
  return exploreAggColumnTypes.includes(value as ExploreAggColumnType);
}

export const exploreColumnTypeToLabel: Record<ExploreColumnType, string> = {
  tenantId: "",
  id: "",
  displayId: "",
  subject: "Subject",
  description: "Description",
  createdAt: "Created At",
  updatedAt: "Updated At",
  status: "Status",
  channel: "Channel",
  topic: "✨ Topic",
  subTopic: "✨ Intent",
  category: "✨ Category",
  agentAssigned: "Agent",
  agentAssignedId: "",
  team: "Team",
  teams: "Teams",
  tenantUser: "User",
  tenantOrganization: "Customer",
  validationStatus: "Validation Status",
  tags: "Tags",
  avgQaScore: "✨ Avg QA Score",
  avgQaScoreRoundRange: "✨ Avg QA Score",
  avgQuackMessageScoreRoundRange: "✨ Avg Quack Message Score",
  avgAutoQaScore: "✨ Avg Auto QA Score",
  avgSentiment: "✨ Avg Sentiment",
  lastSentiment: "✨ Last Sentiment",
  avgQuackMessageScore: "Avg Quack Message Score",
  csat: "CSAT",
  csatComment: "CSAT Comment",
  countPublicMessages: "Public Messages",
  countInternalMessages: "Internal Messages",
  timeToResolution: "TTR",
  timeToFirstResponse: "FTR",
  resolutionStatus: "Resolution Status",
  escalationReason: "Escalation Reason",
  skipReason: "Skipped Reason",
  resolvedReason: "Resolved Reason",
  countQuackMessages: "Quack Messages",
  isQuackEscalated: "Quack Escalated",
  isQuackAnswered: "Quack Answered",
  isAnsweredByAui: "Answered by AUI",
  collaborators: "Collaborators",
  isFiniteState: "",
  ticketId: "Ticket",
  originAuthor: "Origin",
  originUrl: "Origin URL",
  scoreTitle: "Score Title",
  scoreContent: "Score Content",
  simulatorId: "AI Agent",
  skillUsed: "Skill Used",
  skillsUsed: "Skills Used",
  isUserAuthenticated: "Is User Authenticated",
  avgCsat: "Avg CSAT",
};

export const exploreAggColumnTypeToLabel: Record<ExploreAggColumnType, string> =
  {
    volume: "Volume",
    volumeRatio: "%Volume",
    avgQuackMessages: "Avg Quack Messages",
    avgQaScore: "Avg QA Score",
    avgAutoQaScore: "Avg Auto QA Score",
    avgSentiment: "Avg Sentiment",
    avgCsat: "Avg CSAT",
    avgPublicMessages: "Avg Public Messages",
    avgInternalMessages: "Avg Internal Messages",
    countAgentsAssigned: "Agents Evaluated",
    avgTimeToResolution: "Avg TTR",
    avgTimeToFirstResponse: "Avg FRT",
    avgQaScoreByTitle: "Avg QA Score by Title",
    avgQaScoreByContent: "Avg QA Score by Content",
  };

export const exploreGroupByColumnTypeToLabel: Record<
  ExploreGroupByColumnType,
  { singular: string; plural: string }
> = {
  tenantId: { singular: "", plural: "" },
  status: { singular: "Status", plural: "Statuses" },
  channel: { singular: "Channel", plural: "Channels" },
  topic: { singular: "Topic", plural: "Topics" },
  subTopic: { singular: "Intent", plural: "Intents" },
  category: { singular: "Category", plural: "Categories" },
  agentAssigned: { singular: "Agent", plural: "Agents" },
  team: { singular: "Team", plural: "Teams" },
  week: { singular: "Week", plural: "Weeks" },
  day: { singular: "Day", plural: "Days" },
  month: { singular: "Month", plural: "Months" },
  avgQaScore: { singular: "Avg QA Score", plural: "Avg QA Scores" },
  avgQaScoreRoundRange: {
    singular: "Avg QA Score",
    plural: "Avg QA Scores",
  },
  avgQuackMessageScoreRoundRange: {
    singular: "Avg Quack Message Score",
    plural: "Avg Quack Message Scores",
  },
  avgSentiment: { singular: "Avg Sentiment", plural: "Avg Sentiments" },
  isQuackAnswered: { singular: "Quack Answered", plural: "Quack Answered" },
  isAnsweredByAui: { singular: "Answered by AUI", plural: "Answered by AUI" },
  resolutionStatus: {
    singular: "Resolution Status",
    plural: "Resolution Statuses",
  },
  escalationReason: {
    singular: "Escalation Reason",
    plural: "Escalation Reasons",
  },
  skipReason: {
    singular: "Skipped Reason",
    plural: "Skipped Reasons",
  },
  resolvedReason: {
    singular: "Resolved Reason",
    plural: "Resolved Reasons",
  },
  originUrl: {
    singular: "Origin URL",
    plural: "Origin URLs",
  },
  scoreTitle: {
    singular: "Score Title",
    plural: "Score Titles",
  },
  scoreContent: {
    singular: "Score Content",
    plural: "Score Contents",
  },
  simulator: {
    singular: "AI Agent",
    plural: "AI Agents",
  },
  skillUsed: {
    singular: "Skill Used",
    plural: "Skills Used",
  },
  avgCsat: { singular: "CSAT", plural: "CSAT" },
};

export const exploreGroupByToColumn: Record<
  ExploreGroupByColumnType,
  ExploreColumnType | null
> = {
  tenantId: "tenantId",
  status: "status",
  channel: "channel",
  topic: "topic",
  category: "category",
  resolutionStatus: "resolutionStatus",
  escalationReason: "escalationReason",
  skipReason: "skipReason",
  resolvedReason: "resolvedReason",
  subTopic: "subTopic",
  agentAssigned: "agentAssigned",
  team: "teams",
  week: null,
  day: null,
  month: null,
  avgQaScore: "avgQaScore",
  avgQaScoreRoundRange: "avgQaScoreRoundRange",
  avgQuackMessageScoreRoundRange: "avgQuackMessageScoreRoundRange",
  avgSentiment: "avgSentiment",
  isQuackAnswered: "isQuackAnswered",
  isAnsweredByAui: "isAnsweredByAui",
  originUrl: "originUrl",
  scoreTitle: null,
  scoreContent: null,
  simulator: null,
  skillUsed: null,
  avgCsat: "avgCsat",
};
